import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";

import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import { ListItemIcon, ListItemText } from "../StyledComponents";
import { ThemedPlayArrowIcon } from "assets/CXFIcons/Designer/ContextActions";
import CircularProgress from "@mui/material/CircularProgress";

import { executeFlow } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { executorSocket } from "layouts/Flow Designer/components/Sockets";
import { clearNodesHighlight } from "layouts/Flow Designer/helpers/common";
import { NODE_TYPES, REQUIRED_CREDENTIAL_COMPONENTS } from "constants/flowDesigner";
import { hideComponentPropsPanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { setIsTitlebarExecuteTriggerListOpen, setExecuteSelectedTriggerId } from "redux/actions/flowDesigner/triggersActionCreator";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { tryCatchWrapper } from "helpers/common";
import { CancelExecutionDialog } from "./CancelExecutionDialog";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";
import { THEME_COLORS } from "constants/theme";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "assets/icons/pro-light-svg-icons";
import Tooltip from "@mui/material/Tooltip";

const ExecuteFlowButton = ({ compact = false }) => {
   const { t } = useTranslation("flowDesigner");
   const { isExecutorSocketDown } = useSelector((store) => store.designer.flowExecution);
   const dispatch = useDispatch();
   const { isExecuting } = useSelector((store) => store?.designer?.tabs?.currentFlowTab);
   const { canvasFlowInstance, currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { flowExecutionFailed } = useSelector((store) => store?.designer?.flowExecution);
   const { showComponentPropsPanel } = useSelector((store) => store?.designer?.componentPropsPanel);
   const { user } = useAuth0();
   const [showDialog, setShowDialog] = useState(false);
   const { addLayoutTab } = useFlexLayout();
   const theme = useTheme();

   const getNodesWithInvalidCredentials = () => {
      const { nodes } = canvasFlowInstance;
      const nodesWithInvalidCredentials = [];

      nodes.forEach((node) => {
         if (node.type === NODE_TYPES.STATIC) return;

         if (node.data.isDisabled) return;

         const nodeFlowElementData = node.data?.flowElement?.data;

          const isCredentialRequired = REQUIRED_CREDENTIAL_COMPONENTS.has(node.data?.type) || nodeFlowElementData?.credentialRequired?.value;

          if (isCredentialRequired && !nodeFlowElementData?.credential?.value) {
            nodesWithInvalidCredentials.push(node);
         }
      });

      return nodesWithInvalidCredentials;
   };

   const checkForAnyFlowErrors = () => {
      const nodesWithInvalidCredentials = getNodesWithInvalidCredentials();

      if (nodesWithInvalidCredentials.length) {
         toast.error(
            <SoftBox sx={{ paddingLeft: "8px" }}>
               <SoftTypography color="text" sx={{ marginBottom: "8px", fontSize: "15px", fontWeight: 700 }}>
                  Please enter the following node credentials:
               </SoftTypography>
               <ul style={{ paddingLeft: "1rem" }}>
                  {nodesWithInvalidCredentials.map((node) => (
                     <li key={node.data?.componentKey}>
                        <SoftTypography sx={{ fontSize: "13px" }}>{node.data?.componentKey}</SoftTypography>
                     </li>
                  ))}
               </ul>
            </SoftBox>,
            { pauseOnHover: true }
         );
         hideComponentProps();

         return true;
      }

      return false;
   };

   const handleLogsTab = (tab) => () => {
      addLayoutTab(tab, `executionReport`, `Logs`);
   };

   const handleExecute = async () => {
      if (isExecutorSocketDown) return;
      if (isExecuting) {
         setShowDialog(true);
         return;
      }

      if (!currentSavedFlow) {
         toast.error(t("messages.not_saved_flow"));
         return;
      }

      if (!executorSocket?.connected) {
         toast.error(t("messages.no_websocket_connection"));
         return;
      }

      const { nodes } = canvasFlowInstance;
      const nonStaticNodes = nodes.filter((node) => node.type !== NODE_TYPES.STATIC);

      if (nonStaticNodes.length === 0) {
         toast.warning(t("messages.add_components"));
         return;
      }

      const enabledNodes = nonStaticNodes.filter((node) => !node.data?.isDisabled);

      if (enabledNodes.length === 0) {
         return toast.warning(t("messages.enable_components"));
      }

      const hasErrors = tryCatchWrapper(checkForAnyFlowErrors)();

      if (hasErrors) return;

      clearNodesHighlight();
      dispatch(
         executeFlow(user?.stripeId, {
            flow_id: currentSavedFlow.id,
            targetUserId: user?.cxfUserId
         })
      );

      handleLogsTab(FLEX_LAYOUT_COMPONENT.executionReport)();
      dispatch(setExecuteSelectedTriggerId(null));
      dispatch(setIsTitlebarExecuteTriggerListOpen(false));
      hideComponentProps();
   };

   const closeDialog = () => {
      setShowDialog(false);
   };

   useEffect(() => {
      if (flowExecutionFailed) {
         toast.error(t("messages.execution_failed"));
      }
   }, [flowExecutionFailed]);

   const hideComponentProps = () => {
      if (showComponentPropsPanel) {
         dispatch(hideComponentPropsPanel());
      }
   };

   return (
      <>
         <ListItem sx={compact ? { p: 0, width: "100%" } : { marginRight: "0.4rem", height: "100%" }}>
            <ListItemButton
               id="btnExecuteFlow"
               className={compact ? "titlebar__menu-item" : "commandbar-item-btn"}
               sx={{
                  borderRadius: compact ? "9px" : "4px",
                  height: compact ? "auto" : "100%",
                  "&:hover": {
                     backgroundColor: THEME_COLORS[theme.palette?.mode?.toUpperCase()].SELECTED_BG
                  }
               }}
               onClick={handleExecute}
               disabled={isExecutorSocketDown}
            >
               <ListItemIcon
                  className={compact ? "titlebar__menu-icon" : "commandbar-icon-wrapper"}
                  sx={{
                     minWidth: "unset",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     width: 24,
                     height: 24
                  }}
               >
                  <span
                     style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 18,
                        height: 18
                     }}
                  >
                     {isExecuting ? <CircularProgress size={14} /> : <ThemedPlayArrowIcon sx={{ fontSize: "18px !important" }} />}
                  </span>
               </ListItemIcon>
               <ListItemText sx={compact ? { width: "auto", m: 0 } : { width: "60px" }} primary={isExecuting ? t("buttons.cancel") : t("menus.execute")} />
            </ListItemButton>
            {isExecutorSocketDown && (
               <div style={{ marginRight: "12px", transform: "translateX(-4px)" }}>
                  <Tooltip title="Please wait while the Flow Designer is reconnecting to the flow execution engine">
                     <FontAwesomeIcon icon={faTriangleExclamation} style={{ width: "14px", color: "#FFA500" }} />
                  </Tooltip>
               </div>
            )}
         </ListItem>
         <CancelExecutionDialog showDialog={showDialog} closeDialog={closeDialog} />
      </>
   );
};

export default ExecuteFlowButton;
